import * as React from 'react'

import { MaskedField, type MaskedFieldProps } from './MaskedField'

/**
 * Campos mascarados do catálogo.
 *
 * As máscaras vivem aqui, e não em quem consome, porque são formatação — não
 * regra de negócio. Antes desta camada, cada tela que precisasse de CPF ou CEP
 * repetia a string de máscara na chamada, e a única implementação existente
 * morava dentro de `features/user`.
 *
 * O que **não** entra aqui é contrato de API. Derivar DDD e número a partir do
 * telefone, por exemplo, é formato de payload e pertence à feature.
 */

const CPF_MASK = '999.999.999-99'
const CNPJ_MASK = '99.999.999/9999-99'

type DocumentFieldProps = Omit<MaskedFieldProps, 'mask'> & {
  /**
   * `auto` alterna entre CPF e CNPJ conforme o comprimento digitado — é o
   * default porque quem preenche costuma saber qual documento tem, e travar o
   * formato erra sempre que a suposição é outra.
   */
  format?: 'cpf' | 'cnpj' | 'auto'
}

export const DocumentField = React.forwardRef<HTMLInputElement, DocumentFieldProps>(
  ({ format = 'auto', placeholder, ...props }, ref) => {
    const mask =
      format === 'cpf' ? CPF_MASK : format === 'cnpj' ? CNPJ_MASK : [CPF_MASK, CNPJ_MASK]

    return (
      <MaskedField
        ref={ref}
        mask={mask}
        placeholder={placeholder ?? (format === 'cnpj' ? '00.000.000/0000-00' : '000.000.000-00')}
        {...props}
      />
    )
  }
)
DocumentField.displayName = 'DocumentField'

/**
 * Telefone brasileiro. As duas máscaras cobrem fixo (8 dígitos) e celular (9),
 * e o `use-mask-input` troca pelo comprimento — a versão anterior decidia isso
 * lendo o terceiro caractere do valor na mão.
 *
 * Com `unmasked`, o formulário recebe só os dígitos. É o que a API espera:
 * `phone_prefix` e `phone` são dígitos puros, e quem monta esse payload
 * (`features/user/additional-information`) fatia a partir daqui. A divisão em
 * DDD e número continua sendo da feature — isto é formato de payload, não
 * formatação de campo.
 */
export const PhoneField = React.forwardRef<
  HTMLInputElement,
  Omit<MaskedFieldProps, 'mask'>
>(({ placeholder, ...props }, ref) => (
  <MaskedField
    ref={ref}
    mask={['(99) 9999-9999', '(99) 99999-9999']}
    inputMode="tel"
    placeholder={placeholder ?? '(00) 00000-0000'}
    {...props}
  />
))
PhoneField.displayName = 'PhoneField'

export const PostalCodeField = React.forwardRef<
  HTMLInputElement,
  Omit<MaskedFieldProps, 'mask'>
>(({ placeholder, ...props }, ref) => (
  <MaskedField
    ref={ref}
    mask="99999-999"
    placeholder={placeholder ?? '00000-000'}
    {...props}
  />
))
PostalCodeField.displayName = 'PostalCodeField'
